import * as babylon from "@babylonjs/core";

export interface GradientOptions {
  colorLow: babylon.Color3;
  colorHigh: babylon.Color3;
  minMagnitude: number;
  maxMagnitude: number;
}

/**
 * Where `value` falls in `min`..`max`, from 0 at `min` to 1 at `max`, clamped
 * to that span. A range with no width — or with its ends crossed, which leaves
 * none either — gives the midpoint, 0.5, so every value draws in the middle
 * color rather than at one arbitrary end.
 */
export function gradientFraction(
  value: number,
  min: number,
  max: number,
): number {
  const range = max - min;
  const t = range > 0 ? (value - min) / range : 0.5;
  return Math.max(0, Math.min(1, t));
}

/**
 * Returns the gradient color for a given magnitude, interpolated between
 * colorLow and colorHigh based on the magnitude's position in the range.
 */
export function interpolateGradientColor(
  gradient: GradientOptions,
  magnitude: number,
): babylon.Color3 {
  return babylon.Color3.Lerp(
    gradient.colorLow,
    gradient.colorHigh,
    gradientFraction(magnitude, gradient.minMagnitude, gradient.maxMagnitude),
  );
}

/**
 * Blends `low` into `high` in HSL — hue, saturation and lightness — rather
 * than channel by channel. Returns a function that writes the color `t` of
 * the way from `low` to `high` into `result`.
 *
 * Hue takes the shorter way round the color wheel, crossing 0°/360° when that
 * is shorter: blue (240°) to red (0°, or 360°) passes through magenta, 120°
 * away, not through cyan, green and yellow, 240° away. Two hues exactly
 * opposite each other, as far apart one way as the other, take the way that
 * does not cross 0°: blue to yellow (60°) passes through green, and so does
 * yellow to blue. Swapping the ends runs the same route backwards.
 *
 * A color with no hue has none to lend. Gray takes the other end's hue, so
 * gray to green stays green. Black and white, which have no saturation either,
 * take the other end's hue and saturation, so black to green is ever brighter
 * green and white to green fades in green.
 *
 * Both ends are converted once, here, since a point cloud blends tens of
 * thousands of points between the same two colors.
 */
export function hslBlend(
  low: babylon.Color3,
  high: babylon.Color3,
): (t: number, result: babylon.Color3) => babylon.Color3 {
  const lowHsl = toHsl(low);
  const highHsl = toHsl(high);
  const from = withMissingHueFrom(lowHsl, highHsl);
  const to = withMissingHueFrom(highHsl, lowHsl);
  // The signed turn from `from` to `to`, in [-180, 180]: the plain difference,
  // which never crosses 0°, unless that is more than half the wheel, when the
  // other way round, crossing 0°, is shorter. A tie, exactly half, keeps the
  // plain difference, which is the same arc whichever end it starts from;
  // sending both ends the decreasing way would draw another palette once the
  // ends were swapped.
  const direct = to.hue - from.hue;
  const turn =
    Math.abs(direct) <= 180 ? direct : direct - Math.sign(direct) * 360;
  return (t, result) =>
    hslToRgbToRef(
      from.hue + turn * t,
      from.saturation + (to.saturation - from.saturation) * t,
      from.lightness + (to.lightness - from.lightness) * t,
      result,
    );
}

interface Hsl {
  /** Degrees, in [0, 360). */
  hue: number;
  saturation: number;
  lightness: number;
}

/**
 * `hsl` with what it lacks taken from `other`: black or white takes the other
 * color's hue and saturation, gray its hue.
 */
function withMissingHueFrom(hsl: Hsl, other: Hsl): Hsl {
  if (hsl.lightness === 0 || hsl.lightness === 1) {
    return { ...hsl, hue: other.hue, saturation: other.saturation };
  }
  if (hsl.saturation === 0) {
    return { ...hsl, hue: other.hue };
  }
  return hsl;
}

/** An RGB color as HSL. A color with no chroma reads as hue 0, saturation 0. */
function toHsl({ r, g, b }: babylon.Color3): Hsl {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const chroma = max - min;
  if (chroma === 0) {
    return { hue: 0, saturation: 0, lightness };
  }
  const saturation = chroma / (1 - Math.abs(2 * lightness - 1));
  let sector: number;
  if (max === r) {
    sector = (g - b) / chroma;
  } else if (max === g) {
    sector = (b - r) / chroma + 2;
  } else {
    sector = (r - g) / chroma + 4;
  }
  return { hue: (sector * 60 + 360) % 360, saturation, lightness };
}

/**
 * Writes the RGB of an HSL color into `result`. `hue` is in degrees and may lie
 * outside [0, 360), as a blend crossing 0° leaves it.
 */
function hslToRgbToRef(
  hue: number,
  saturation: number,
  lightness: number,
  result: babylon.Color3,
): babylon.Color3 {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = (((hue % 360) + 360) % 360) / 60;
  const second = chroma * (1 - Math.abs((sector % 2) - 1));
  const m = lightness - chroma / 2;
  if (sector < 1) {
    return result.set(chroma + m, second + m, m);
  }
  if (sector < 2) {
    return result.set(second + m, chroma + m, m);
  }
  if (sector < 3) {
    return result.set(m, chroma + m, second + m);
  }
  if (sector < 4) {
    return result.set(m, second + m, chroma + m);
  }
  if (sector < 5) {
    return result.set(second + m, m, chroma + m);
  }
  return result.set(chroma + m, m, second + m);
}
